import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";

import * as integrationActions from "../../../redux/Integration/actions.js";
import { buildFieldMappingsRequestBody, buildAutoSaveRequestBody } from "./utils/fieldMappingConverter";

import PageBase from "../../../components/PageBase";
import * as ROUTES from "../../../utils/routes";
import constants from "../../../utils/constants.js";

// Components
import HubspotHeader from "./components/HubspotHeader";
import IntegrationInfo from "./components/IntegrationInfo";
import TabNavigation from "./components/TabNavigation";
import MappingTab from "./components/MappingTab";
import AutomationTab from "./components/AutomationTab";
import DisconnectModal from "./components/modals/DisconnectModal";
import DeleteMappingModal from "./components/modals/DeleteMappingModal";
import DuplicateMappingModal from "./components/modals/DuplicateMappingModal";
import AddMappingModal from "./components/modals/AddMappingModal";
import CreateTriggerModal from "./components/modals/CreateTriggerModal";
import PicklistMappingModal from "./components/modals/PicklistMappingModal";
import ConnectedModal from "./components/modals/ConnectedModal";
import ConnectToAceModal from "./components/modals/ConnectToAceModal";
import ToastContainer from "../../../components/Toast/ToastContainer";

// Custom Hooks
import { useMappingProfiles } from "./hooks/useMappingProfiles";
import { useFieldMappings } from "./hooks/useFieldMappings";
import { useFilters } from "./hooks/useFilters";
import { useTriggers } from "./hooks/useTriggers";
import { useToast } from "../../../hooks/useToast";
import { mapHubspotPropertiesToSourceFields } from "./utils/mappingHelpers";
import { mockSourceFields } from "../../../utils/mappingMockData";

// Styles
import "./hubspot-integration.scss";

// Constants
const DEAL_STAGE_API_NAME = "dealstage";
const DEAL_STAGE_LABEL = "Deal Stage";
const DEALS_OBJECT_TYPE = "deals";
const parseSourceFieldApiName = (value) => {
  if (!value) return { apiName: "", objectLabel: "" };
  const parts = String(value).split(".");
  if (parts.length > 1) {
    return { objectLabel: parts[0].toLowerCase(), apiName: parts.slice(1).join(".") };
  }
  return { apiName: value, objectLabel: "" };
};

const HubspotIntegration = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Tab state
  const [activeTab, setActiveTab] = useState("Mapping");

  // Modal states
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [showDeleteMappingModal, setShowDeleteMappingModal] = useState(false);
  const [deleteTargetProfile, setDeleteTargetProfile] = useState(null);
  const [deleteTargetProfileId, setDeleteTargetProfileId] = useState(null);
  const [showDuplicateMappingModal, setShowDuplicateMappingModal] = useState(false);
  const [showAddMappingModal, setShowAddMappingModal] = useState(false);
  const [showConnectToAceModal, setShowConnectToAceModal] = useState(false);
  const [showCreateTriggerModal, setShowCreateTriggerModal] = useState(false);
  const [editingTriggerId, setEditingTriggerId] = useState(null);
  const [triggerModalData, setTriggerModalData] = useState(null);
  const [showPicklistModal, setShowPicklistModal] = useState(false);
  const [picklistModalData, setPicklistModalData] = useState(null);

  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);

  // Action loading states
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMakingDefault, setIsMakingDefault] = useState(false);
  const [isCreatingProfile, setIsCreatingProfile] = useState(false);
  const [isSavingProfileName, setIsSavingProfileName] = useState(false);

  // Auto-save debounce timer
  const autoSaveTimerRef = useRef(null);

  // Cache for field mappings data per profile (for duplication)
  const [fieldMappingsCache, setFieldMappingsCache] = useState({});

  // Automation toggle states
  const [autoAcceptAWSReferrals, setAutoAcceptAWSReferrals] = useState(true);
  const [autoSyncOpportunity, setAutoSyncOpportunity] = useState(true);
  const [automatedOpportunitySharing, setAutomatedOpportunitySharing] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  const integrationConnected = searchParams.get("integration_connected") === "true";
  const [showConnectedModal, setShowConnectedModal] = useState(integrationConnected);
  const [connection, setConnection] = useState(null);
  const [sourceFields, setSourceFields] = useState([]);
  const [isLoadingProperties, setIsLoadingProperties] = useState(false);
  const [stagePipelines, setStagePipelines] = useState([]);
  const [solutions, setSolutions] = useState([]);

  // Toast notifications
  const { toasts, removeToast, showSuccess, showError, showWarning } = useToast();

  // Fetch mapping profiles from API
  const fetchMappingsList = useCallback(
    (callback) => {
      const crm_id = localStorage.getItem(constants.CRM_ID);
      dispatch(
        integrationActions.getFieldMappingsList(
          { vendor: "hubspot", crm_id: crm_id },
          callback
        )
      );
    },
    [dispatch]
  );

  // Fetch field mapping detail from API
  const fetchFieldMappingDetail = useCallback(
    (id, callback) => {
      dispatch(
        integrationActions.getFieldMappingDetail(
          { id },
          callback
        )
      );
    },
    [dispatch]
  );

  // Delete field mapping from API
  const deleteFieldMappingFn = useCallback(
    (id, callback) => {
      dispatch(
        integrationActions.deleteFieldMapping(
          { id },
          callback
        )
      );
    },
    [dispatch]
  );

  // Make default field mapping
  const makeDefaultFn = useCallback(
    (id, callback) => {
      dispatch(
        integrationActions.makeDefaultFieldMapping(
          { id },
          callback
        )
      );
    },
    [dispatch]
  );

  // Custom hooks
  const {
    mappingProfiles,
    setMappingProfiles,
    activeProfile,
    setActiveProfile,
    mappingNames,
    setMappingNames,
    handleAddMappingProfile,
    handleDuplicateMappingProfile,
    handleDeleteMappingProfile,
    handleRenameMappingProfile,
    handleMakeDefault,
    updateProfileIdAfterCreation,
    commitProfileName,
    refreshProfiles,
    refreshProfilesAndKeepActive,
    isLoading: isLoadingProfiles,
  } = useMappingProfiles("hubspot", fetchMappingsList, deleteFieldMappingFn, makeDefaultFn, {
    showSuccess,
    showError,
    showWarning,
  }, connection);

  const {
    fieldMappings,
    picklistMappings,
    pipelinePicklistMappings,
    handleMappingChange,
    handleDefaultValueChange,
    handlePicklistMappingsSave,
    prepareDuplicatedData,
    isLoading: isLoadingFieldMappings,
    isEmptyMapping
  } = useFieldMappings(activeProfile, fetchFieldMappingDetail);

  const { statusFilter, setStatusFilter, mappingFilter, setMappingFilter } =
    useFilters();

  const {
    triggers,
    handleDeactivateTrigger,
    handleDeleteTrigger,
    handleSaveTrigger,
  } = useTriggers();

  // Cache field mappings data whenever they change
  useEffect(() => {
    if (activeProfile && fieldMappings.length > 0) {
      setFieldMappingsCache((prev) => ({
        ...prev,
        [activeProfile]: {
          fieldMappings: JSON.parse(JSON.stringify(fieldMappings)), // Deep clone
          picklistMappings: JSON.parse(JSON.stringify(picklistMappings)), // Deep clone
          pipelinePicklistMappings: JSON.parse(JSON.stringify(pipelinePicklistMappings)), // Deep clone
        },
      }));
    }
  }, [activeProfile, fieldMappings, picklistMappings, pipelinePicklistMappings]);

  // Show duplicate modal
  const handleDuplicateMappingProfileWithData = () => {
    if (!activeProfile) {
      showWarning("Please select a mapping profile first.");
      return;
    }

    // Check if profile is saved (not temporary)
    const isNewProfile = activeProfile.startsWith("profile-");
    if (isNewProfile) {
      showWarning("Please save this profile first before duplicating.");
      return;
    }

    setShowDuplicateMappingModal(true);
  };

  // Handle duplicate confirmation from modal
  const handleConfirmDuplicate = (newName) => {
    setShowDuplicateMappingModal(false);
    setIsDuplicating(true);

    // Call API to duplicate
    dispatch(
      integrationActions.duplicateFieldMapping(
        { id: activeProfile, name: newName },
        (response) => {
          setIsDuplicating(false);

          if (response && response.status === "ok" && response.data) {
            const newProfile = response.data;

            // Add new profile to list
            const newProfileObj = {
              id: newProfile.id,
              name: newProfile.name,
              isDefault: newProfile.is_default === 1,
              createdAt: newProfile.created_at,
              updatedAt: newProfile.updated_at,
            };

            setMappingProfiles((prev) => [...prev, newProfileObj]);
            setMappingNames((prev) => ({
              ...prev,
              [newProfile.id]: newProfile.name,
            }));

            // Switch to new profile
            setActiveProfile(newProfile.id);

            showSuccess(`Mapping profile "${newName}" created successfully!`);
          } else {
            const errorMessage = response?.message || "Failed to duplicate mapping profile. Please try again.";
            showError(errorMessage);
          }
        }
      )
    );
  };

  // Show add new mapping modal
  const handleAddMappingProfileWithModal = () => {
    setShowAddMappingModal(true);
  };

  // Handle add new mapping confirmation from modal
  const handleConfirmAddNew = (newName) => {
    setShowAddMappingModal(false);
    setIsCreatingProfile(true);

    // Call API to create new mapping
    dispatch(
      integrationActions.saveFieldMappings(
        {
          vendor: "hubspot",
          body: {
            name: newName,
            mapping_json: [],
            picklist_mapping_json: {},
            pipeline_picklist_mappings: [],
            crm_id: connection?.crm_source_id || null,
          },
        },
        (response) => {
          setIsCreatingProfile(false);

          if (response && response.status === "ok" && response.data) {
            const newProfile = response.data;

            // Add new profile to list
            const newProfileObj = {
              id: newProfile.id,
              name: newProfile.name,
              isDefault: newProfile.is_default === 1,
              createdAt: newProfile.created_at,
              updatedAt: newProfile.updated_at,
            };

            // Remove mock profile if it exists and add the new real profile
            setMappingProfiles((prev) => {
              const filteredProfiles = prev.filter(p => !p.isMock);
              return [...filteredProfiles, newProfileObj];
            });

            setMappingNames((prev) => {
              // Remove mock profile names
              const filteredNames = {};
              Object.keys(prev).forEach(key => {
                const profile = mappingProfiles.find(p => p.id === key);
                if (!profile || !profile.isMock) {
                  filteredNames[key] = prev[key];
                }
              });
              return {
                ...filteredNames,
                [newProfile.id]: newProfile.name,
              };
            });

            // Switch to new profile
            setActiveProfile(newProfile.id);

            showSuccess(`Mapping profile "${newName}" created successfully!`);
          } else {
            const errorMessage = response?.message || "Failed to create mapping profile. Please try again.";
            showError(errorMessage);
          }
        }
      )
    );
  };

  // Wrapper for make default that shows success message
  const handleMakeDefaultWithReload = (profileId = null) => {
    setIsMakingDefault(true);

    handleMakeDefault(
      () => {
        // Success callback - The hook will automatically reload the list via refreshTrigger
        setIsMakingDefault(false);
        showSuccess("Profile set as default successfully!");
      },
      () => {
        // Error callback
        setIsMakingDefault(false);
      },
      profileId
    );
  };

  // Wrapper for delete that shows confirmation modal
  const handleDeleteMappingProfileWithConfirm = (profileId = null) => {
    // Use provided profileId or fallback to activeProfile
    const targetProfileId = profileId || activeProfile;
    const currentProfile = mappingProfiles.find((p) => p.id === targetProfileId);
    if (!currentProfile) {
      return;
    }

    // Check if it's a new profile (not saved to API)
    const isNewProfile = targetProfileId.startsWith("profile-");

    if (isNewProfile) {
      // New profiles don't need modal confirmation, delete directly
      handleDeleteMappingProfile(targetProfileId);
    } else {
      // Saved profiles need modal confirmation
      setDeleteTargetProfile(currentProfile);
      setShowDeleteMappingModal(true);
      // Store the profileId to use when confirming delete
      setDeleteTargetProfileId(targetProfileId);
    }
  };

  // Confirm delete from modal
  const confirmDeleteMapping = () => {
    setShowDeleteMappingModal(false);
    setIsDeleting(true);

    // Use stored profileId or fallback to activeProfile
    const targetProfileId = deleteTargetProfileId || activeProfile;

    // Handle delete with loading state
    handleDeleteMappingProfile(targetProfileId);

    // Set a timeout to reset loading state (since the hook handles the actual deletion)
    setTimeout(() => {
      setIsDeleting(false);
      setDeleteTargetProfile(null);
      setDeleteTargetProfileId(null);
    }, 500);
  };

  // Disconnect handler
  const handleDisconnect = () => {
    console.log("Disconnecting HubSpot");
    dispatch(integrationActions.disconnectMyconnection(() => {
      let menuData = localStorage.getItem(constants.MENU_DATA);
      try {
        menuData = JSON.parse(menuData);
      } catch (error) {
      }

      const openCategory = menuData ? menuData?.openCategory : null;
      const route = ROUTES.INTEGRATIONS;
      localStorage.setItem(constants.MENU_DATA, JSON.stringify({ openCategory, route }));

      setShowDisconnectModal(false);
      navigate(ROUTES.INTEGRATIONS);
    }));
  };

  // Trigger handlers
  const handleCreateTrigger = () => {
    setEditingTriggerId(null);
    setTriggerModalData(null);
    setShowCreateTriggerModal(true);
  };

  const handleEditTrigger = (triggerId) => {
    const trigger = triggers.find((t) => t.id === triggerId);
    if (!trigger) return;

    setEditingTriggerId(triggerId);
    setTriggerModalData({
      name: trigger.name,
      status: trigger.status === "Active",
      autoTrigger: trigger.autoTrigger,
      sequences: trigger.sequences,
    });
    setShowCreateTriggerModal(true);
    console.log("Editing trigger:", trigger.name);
  };

  const handleSaveTriggerWrapper = (triggerData, editingId) => {
    handleSaveTrigger(triggerData, editingId);
    setShowCreateTriggerModal(false);
    setEditingTriggerId(null);
    setTriggerModalData(null);
  };

  // Picklist mapping handlers
  const handleMapValues = (targetFieldApiName) => {
    const mapping = fieldMappings.find(
      (m) => m.targetField.apiName === targetFieldApiName
    );

    if (!mapping) {
      console.error("Mapping not found for field:", targetFieldApiName);
      return;
    }

    const sourceFieldApiName = mapping.sourceFieldApiName;

    // Check if source field is selected
    if (!sourceFieldApiName || sourceFieldApiName === "") {
      console.warn("No source field selected for picklist mapping");
      return;
    }

    // Get ACE picklist values from target field
    const acePicklistValues = mapping.targetField.picklistValues
      ? mapping.targetField.picklistValues.map((pv) => pv.label || pv.value || pv)
      : [];

    // Get HubSpot picklist values from source field
    // Parse source field value to detect object prefix (e.g., "Companies.industry")
    const { apiName: cleanApiName, objectLabel: desiredObjectLabel } = parseSourceFieldApiName(sourceFieldApiName);

    // Find source field (handle duplicate apiName across objects, e.g., industry on Contacts vs Companies)
    const sourceFieldCandidates = sourceFields.filter((sf) => sf.apiName === cleanApiName);
    const sourceField =
      // 1) If objectLabel is known from the value, use that first
      sourceFieldCandidates.find((sf) => desiredObjectLabel && sf.objectLabel === desiredObjectLabel) ||
      // 2) Prefer candidate with options (picklists)
      sourceFieldCandidates.find((sf) => Array.isArray(sf.options) && sf.options.length > 0) ||
      // 3) Fallback to first
      sourceFieldCandidates[0];
    const hubspotPicklistValues = sourceField?.options
      ? sourceField.options.map((opt) => {
        return {
          value: opt.value || opt,
          label: opt.label || opt.value || opt,
        }
      })
      : [];

    // Debug log for empty picklist options (e.g., companies.industry)
    if (hubspotPicklistValues.length === 0) {
      // eslint-disable-next-line no-console
      console.log("Picklist options empty for source field", {
        sourceFieldApiName,
        sourceField,
      });
    }

    // Check if source field is deal stage
    // Based on HubSpot API response: name="dealstage" or label="Deal Stage"
    const isDealStage = sourceField && (
      sourceField.apiName === DEAL_STAGE_API_NAME ||
      sourceField.label === DEAL_STAGE_LABEL
    );

    // Load existing mappings from picklistMappings or pipelinePicklistMappings state
    let existingMappings = [];

    // If deal stage with pipelines, get mappings from first pipeline (will be shown when user selects pipeline)
    if (isDealStage && stagePipelines && stagePipelines.length > 0) {
      // Get the first pipeline's mappings as default
      const sortedPipelines = [...stagePipelines].sort(
        (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
      );
      if (sortedPipelines.length > 0) {
        const firstPipelineId = sortedPipelines[0].id;
        existingMappings = pipelinePicklistMappings[firstPipelineId]?.[targetFieldApiName] || [];
      }
    } else {
      // Regular picklist mappings (no pipelines)
      existingMappings = picklistMappings[targetFieldApiName] || [];
    }

    setPicklistModalData({
      targetFieldName: targetFieldApiName,
      targetFieldLabel: mapping.targetField.label,
      sourceFieldApiName: sourceFieldApiName,
      acePicklistValues,
      hubspotPicklistValues,
      existingMappings,
      isDealStage: isDealStage || false,
      stagePipelines: isDealStage ? stagePipelines : [],
      pipelinePicklistMappings: isDealStage && stagePipelines.length > 0 ? pipelinePicklistMappings : {},
    });

    setShowPicklistModal(true);
  };

  const handleSavePicklistMappings = (saveData) => {
    console.log("Saving picklist mappings:", saveData);
    handlePicklistMappingsSave(saveData);

    // Get updated mappings from saveData to avoid state async issues
    let updatedPicklistMappings = picklistMappings;
    let updatedPipelinePicklistMappings = pipelinePicklistMappings;

    if (saveData.hasPipelines && saveData.allPipelineMappings) {
      // Pipeline mappings case
      updatedPipelinePicklistMappings = saveData.allPipelineMappings;
    } else if (saveData.mappings && saveData.targetFieldName) {
      // Regular picklist mappings case
      updatedPicklistMappings = {
        ...picklistMappings,
        [saveData.targetFieldName]: saveData.mappings,
      };
    }

    // Set loading state immediately for better UX
    setIsAutoSaving(true);
    // Trigger auto-save after picklist mapping change with updated data
    triggerAutoSaveWithData(updatedPicklistMappings, updatedPipelinePicklistMappings);
  };

  // Wrapper for handleMappingChange to trigger auto-save
  const handleMappingChangeWithAutoSave = (targetFieldApiName, sourceFieldApiName) => {
    // Get old source field value to check if it changed
    const oldMapping = fieldMappings.find(m => m.targetField.apiName === targetFieldApiName);
    const oldSourceFieldApiName = oldMapping ? oldMapping.sourceFieldApiName : null;
    const sourceFieldChanged = oldSourceFieldApiName !== sourceFieldApiName;

    // Call the original handler from hook
    handleMappingChange(targetFieldApiName, sourceFieldApiName);

    // Get updated fieldMappings to avoid state async issues
    const updatedFieldMappings = fieldMappings.map((mapping) =>
      mapping.targetField.apiName === targetFieldApiName
        ? { ...mapping, sourceFieldApiName, isSaved: false }
        : mapping
    );

    // Calculate updated picklist mappings (reset if source field changed)
    let updatedPicklistMappings = picklistMappings;
    let updatedPipelinePicklistMappings = pipelinePicklistMappings;

    if (sourceFieldChanged) {
      // Reset regular picklist mappings
      updatedPicklistMappings = { ...picklistMappings };
      delete updatedPicklistMappings[targetFieldApiName];

      // Reset pipeline picklist mappings for all pipelines
      updatedPipelinePicklistMappings = {};
      Object.keys(pipelinePicklistMappings).forEach((pipelineId) => {
        if (pipelinePicklistMappings[pipelineId]) {
          const pipelineMappings = { ...pipelinePicklistMappings[pipelineId] };
          delete pipelineMappings[targetFieldApiName];
          updatedPipelinePicklistMappings[pipelineId] = pipelineMappings;
        }
      });
    }

    // Set loading state immediately for better UX
    setIsAutoSaving(true);
    // Trigger auto-save with updated data
    triggerAutoSaveWithAllData(updatedFieldMappings, updatedPicklistMappings, updatedPipelinePicklistMappings);
  };

  // Wrapper for handleDefaultValueChange to trigger auto-save (for dropdowns only)
  const handleDefaultValueChangeWithAutoSave = (targetFieldApiName, defaultValue) => {
    // Call the original handler from hook
    handleDefaultValueChange(targetFieldApiName, defaultValue);

    // Get updated fieldMappings to avoid state async issues
    const updatedFieldMappings = fieldMappings.map((mapping) =>
      mapping.targetField.apiName === targetFieldApiName
        ? { ...mapping, defaultValue, isSaved: false }
        : mapping
    );

    // Set loading state immediately for better UX
    setIsAutoSaving(true);
    // Trigger auto-save with updated data
    triggerAutoSaveWithFieldMappings(updatedFieldMappings);
  };

  // Auto-save function with debounce (silent save) - with optional override data for all mappings
  const triggerAutoSaveWithAllData = useCallback((overrideFieldMappings = null, overridePicklistMappings = null, overridePipelinePicklistMappings = null) => {
    // Clear any existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    const mappingName = mappingNames[activeProfile];

    // Use override data if provided, otherwise use state
    const fieldMappingsToUse = overrideFieldMappings !== null
      ? overrideFieldMappings
      : fieldMappings;
    const picklistMappingsToUse = overridePicklistMappings !== null
      ? overridePicklistMappings
      : picklistMappings;
    const pipelineMappingsToUse = overridePipelinePicklistMappings !== null
      ? overridePipelinePicklistMappings
      : pipelinePicklistMappings;

    // Set new timer - auto-save after 1.5 seconds of inactivity
    autoSaveTimerRef.current = setTimeout(() => {
      // Check if this is a new profile (including mock-profile) that needs CREATE API
      const isNewProfile = !activeProfile || activeProfile.startsWith("profile-") || activeProfile.startsWith("mock-profile-");

      if (isNewProfile) {
        const requestBody = buildFieldMappingsRequestBody(
          mappingName,
          fieldMappingsToUse,
          picklistMappingsToUse,
          pipelineMappingsToUse,
          sourceFields
        );

        dispatch(
          integrationActions.saveFieldMappings(
            {
              vendor: "hubspot",
              body: { ...requestBody, crm_id: connection?.crm_source_id || null },
            },
            (response) => {
              setIsAutoSaving(false);

              if (response && response.status === "ok") {
                // Update the local profile ID with the UUID returned from API
                // This prevents duplicate POSTs when clicking Save All again
                if (response.data && response.data.id) {
                  const newProfileId = response.data.id;
                  const isMockProfile = activeProfile.startsWith("mock-profile-");

                  updateProfileIdAfterCreation(activeProfile, newProfileId);
                  // Commit the edited name to profile list (update dropdown display)
                  commitProfileName(newProfileId);
                  console.log(`Profile created with ID: ${newProfileId}`);

                  // If this was a mock profile, reload the list to get the correct isDefault status
                  if (isMockProfile) {
                    refreshProfilesAndKeepActive(newProfileId);
                  }
                }

                showSuccess("Field mappings created successfully!");
              } else {
                console.log("auto save here 3")
                const errorMessage =
                  response?.message || "Failed to create field mappings. Please try again.";
                showError(errorMessage);
              }
            }
          )
        )
      } else {
        if (!mappingName || mappingName.trim() === "") {
          setIsAutoSaving(false);
          return;
        }

        // Use buildAutoSaveRequestBody which excludes the name field
        const requestBody = buildAutoSaveRequestBody(
          fieldMappingsToUse,
          picklistMappingsToUse,
          pipelineMappingsToUse,
          sourceFields
        );

        // Silent update (no success toast)
        dispatch(
          integrationActions.updateFieldMappings(
            {
              id: activeProfile,
              body: requestBody,
            },
            (response) => {
              setIsAutoSaving(false);

              if (response && response.status === "ok") {
                // Show success toast for auto-save
                showSuccess("Field mappings auto-saved successfully!");
              } else {
                // Show error toast
                const errorMessage =
                  response?.message || "Failed to auto-save. Please use Save All.";
                showError(errorMessage);
              }
            }
          )
        );
      }

    }, 1500); // 1.5 seconds debounce
  }, [activeProfile, mappingNames, fieldMappings, picklistMappings, pipelinePicklistMappings, dispatch, showSuccess, showError, updateProfileIdAfterCreation, commitProfileName]);

  // Wrapper functions for easier use
  const triggerAutoSaveWithFieldMappings = useCallback((overrideFieldMappings) => {
    triggerAutoSaveWithAllData(overrideFieldMappings, null, null);
  }, [triggerAutoSaveWithAllData]);

  const triggerAutoSaveWithData = useCallback((overridePicklistMappings, overridePipelinePicklistMappings) => {
    triggerAutoSaveWithAllData(null, overridePicklistMappings, overridePipelinePicklistMappings);
  }, [triggerAutoSaveWithAllData]);

  const triggerAutoSave = useCallback(() => {
    triggerAutoSaveWithAllData(null, null, null);
  }, [triggerAutoSaveWithAllData]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  // Save All handler
  const handleSaveAll = () => {
    if (!activeProfile) {
      showWarning("Please select a mapping profile first.");
      return;
    }

    // Get the mapping name from Field Mapping Name input
    const mappingName = mappingNames[activeProfile];
    if (!mappingName || mappingName.trim() === "") {
      showWarning("Please enter a Field Mapping Name.");
      return;
    }

    setIsSaving(true);

    // Build request body with name from Field Mapping Name input
    const requestBody = buildFieldMappingsRequestBody(
      mappingName,
      fieldMappings,
      picklistMappings,
      pipelinePicklistMappings,
      sourceFields
    );

    console.log("Saving field mappings with body:", requestBody);

    // Detect if this is a new profile (created locally) or existing profile (from API)
    // New profiles have ID format: "profile-{timestamp}"
    // Mock profiles have ID format: "mock-profile-{timestamp}"
    // Existing profiles have UUID format from API
    const isNewProfile = activeProfile.startsWith("profile-") || activeProfile.startsWith("mock-profile-");

    if (isNewProfile) {
      // Create new mapping profile using POST
      console.log("Creating new field mapping profile");
      dispatch(
        integrationActions.saveFieldMappings(
          {
            vendor: "hubspot",
            body: { ...requestBody, crm_id: connection?.crm_source_id || null },
          },
          (response) => {
            setIsSaving(false);

            if (response && response.status === "ok") {
              // Update the local profile ID with the UUID returned from API
              // This prevents duplicate POSTs when clicking Save All again
              if (response.data && response.data.id) {
                const newProfileId = response.data.id;
                updateProfileIdAfterCreation(activeProfile, newProfileId);
                // Commit the edited name to profile list (update dropdown display)
                commitProfileName(newProfileId);
                console.log(`Profile created with ID: ${newProfileId}`);

                // Reload profile list from API to update UI buttons (Duplicate, Delete, Make Default)
                // This ensures buttons are shown correctly based on profile status
                // Keep the newly created profile active after refresh
                refreshProfilesAndKeepActive(newProfileId);
              }

              showSuccess("Field mappings created successfully!");
            } else {
              const errorMessage =
                response?.message || "Failed to create field mappings. Please try again.";
              showError(errorMessage);
            }
          }
        )
      );
    } else {
      // Update existing mapping profile using PUT
      console.log("Updating existing field mapping profile with ID:", activeProfile);
      dispatch(
        integrationActions.updateFieldMappings(
          {
            id: activeProfile,
            body: requestBody,
          },
          (response) => {
            setIsSaving(false);

            if (response && response.status === "ok") {

              // Commit the edited name to profile list (update dropdown display)
              commitProfileName(activeProfile);
              showSuccess("Field mappings updated successfully!");
            } else {
              const errorMessage =
                response?.message || "Failed to update field mappings. Please try again.";
              showError(errorMessage);
            }
          }
        )
      );
    }
  };

  // Save profile name only (from edit mode)
  const handleSaveProfileName = (newName) => {
    if (!activeProfile || activeProfile.startsWith("profile-") || activeProfile.startsWith("mock-profile-")) {
      showWarning("Please select a saved profile first.");
      return;
    }

    if (!newName || newName.trim() === "") {
      showWarning("Profile name cannot be empty.");
      return;
    }

    setIsSavingProfileName(true);

    const requestBody = {
      name: newName.trim(),
    };

    dispatch(
      integrationActions.updateFieldMappings(
        {
          id: activeProfile,
          body: requestBody,
        },
        (response) => {
          setIsSavingProfileName(false);

          if (response && response.status === "ok") {
            // Update mappingNames first, then commit to profile list
            setMappingNames({ ...mappingNames, [activeProfile]: newName.trim() });

            // Update profile name in the list
            const updatedProfiles = mappingProfiles.map((p) =>
              p.id === activeProfile ? { ...p, name: newName.trim() } : p
            );
            setMappingProfiles(updatedProfiles);

            showSuccess("Profile name updated successfully!");
          } else {
            const errorMessage =
              response?.message || "Failed to update profile name. Please try again.";
            showError(errorMessage);
          }
        }
      )
    );
  };

  const handleConnectedModalConfirm = () => {
    setShowConnectedModal(false);
    setSearchParams({});

    let menuData = localStorage.getItem(constants.MENU_DATA);
    try {
      menuData = JSON.parse(menuData);
    } catch (error) {
    }

    const openCategory = menuData ? menuData?.openCategory : null;
    const route = ROUTES.HUBSPOT_INTEGRATION;
    localStorage.setItem(constants.MENU_DATA, JSON.stringify({ openCategory, route }));
  }

  const handleGetConnection = useCallback(() => {
    dispatch(integrationActions.getMyconnection((conn) => {
      if (conn?.crm_status !== "connected") {
        let menuData = localStorage.getItem(constants.MENU_DATA);
        try {
          menuData = JSON.parse(menuData);
        } catch (error) {
        }

        const openCategory = menuData ? menuData?.openCategory : null;

        const route = ROUTES.INTEGRATIONS;

        localStorage.setItem(constants.MENU_DATA, JSON.stringify({ openCategory, route }));

        navigate(route);

        return;
      }

      setConnection(conn);

      localStorage.setItem(constants.CRM_ID, conn.crm_source_id || "");
    }));
  }, [dispatch, navigate]);

  const handleLoadHubspotProperties = useCallback(() => {
    setIsLoadingProperties(true);
    const objectRequests = [
      { type: "deals", label: "Deals" },
      { type: "contacts", label: "Contacts" },
      { type: "companies", label: "Companies" },
      { type: "owner", label: "Owners" },
    ];

    Promise.all(
      objectRequests.map(
        (obj) =>
          new Promise((resolve) => {
            dispatch(
              integrationActions.getCRMProperties(
                { vendor: "hubspot", objectType: obj.type },
                (response) => resolve({ obj, response })
              )
            );
          })
      )
    ).then((results) => {
      const aggregated = [];
      let pipelines = [];

      results.forEach(({ obj, response }) => {
        const props =
          response?.data?.[obj.type] ||
          response?.[obj.type] ||
          response?.data ||
          [];
        const mapped = mapHubspotPropertiesToSourceFields(props, obj.label);
        aggregated.push(...mapped);

        // Extract stage_pipeline from deals response
        if (obj.type === DEALS_OBJECT_TYPE && response?.data?.stage_pipeline) {
          pipelines = response.data.stage_pipeline;
        } else if (obj.type === DEALS_OBJECT_TYPE && response?.stage_pipeline) {
          pipelines = response.stage_pipeline;
        }
      });

      // Fallback to mock if nothing returned
      if (!aggregated.length) {
        setSourceFields(mockSourceFields);
      } else {
        setSourceFields(aggregated);
      }

      setStagePipelines(pipelines);
    }).catch((error) => {
      console.error("Error loading HubSpot properties:", error);
      setSourceFields(mockSourceFields);
      setStagePipelines([]);
    }).finally(() => {
      setIsLoadingProperties(false);
    });
  }, [dispatch]);

  const handleLoadSolutions = useCallback(() => {
    dispatch(
      integrationActions.getSolutions(
        { vendor: "hubspot" },
        (response) => {
          if (response && response.status === "ok" && response.data) {
            // Transform solutions data to options format (only show Active solutions)
            const solutionsOptions = response.data
              .filter((solution) => solution.Status === "Active")
              .map((solution) => ({
                value: `${solution.Id} - ${solution.Name}`,
                label: `${solution.Id} - ${solution.Name}`,
              }));
            // Always add "Other" option at the beginning
            solutionsOptions.unshift({
              value: "Other",
              label: "Other",
            });
            setSolutions(solutionsOptions);
          } else {
            // Even if API fails, still provide "Other" option
            setSolutions([{
              value: "Other",
              label: "Other",
            }]);
          }
        }
      )
    );
  }, [dispatch]);

  const handleSaveConnectToAce = (payload, resetModalLoading) => {
    console.log("Saving Connect to ACE payload:", payload);
    dispatch(integrationActions.connectToAce({ ...payload, connectionId: connection ? connection.id : null }, (response) => {
      if (response && response.status === "ok") {
        handleGetConnection();
        showSuccess("Successfully connected to ACE.");
        setShowConnectToAceModal(false);
      } else {
        showError("Failed to connect to ACE. Please check your credentials and try again.");
      }

      // Reset modal loading state if callback provided
      if (resetModalLoading) {
        resetModalLoading();
      }
    }));
  }


  useEffect(() => {
    handleGetConnection();
    handleLoadHubspotProperties();
    handleLoadSolutions();
  }, [handleGetConnection, handleLoadHubspotProperties, handleLoadSolutions]);

  return (
    <PageBase title="HubSpot Integration" removeMargins>
      <div className="hubspot-integration-container">
        <HubspotHeader />

        <IntegrationInfo
          aceStatus={connection ? connection.ace_status : null}
          onDisconnect={() => setShowDisconnectModal(true)}
          onConnectToAce={() => setShowConnectToAceModal(true)}
        />

        <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />

        {/* Tab Content */}
        {activeTab === "Mapping" && (
          <MappingTab
            // Mapping Profiles
            mappingProfiles={mappingProfiles}
            activeProfile={activeProfile}
            setActiveProfile={setActiveProfile}
            mappingNames={mappingNames}
            handleAddMappingProfile={handleAddMappingProfileWithModal}
            handleDuplicateMappingProfile={handleDuplicateMappingProfileWithData}
            handleDeleteMappingProfile={handleDeleteMappingProfileWithConfirm}
            handleSaveProfileName={handleSaveProfileName}
            handleMakeDefault={handleMakeDefaultWithReload}
            // Field Mappings
            fieldMappings={fieldMappings}
            handleMappingChange={handleMappingChangeWithAutoSave}
            handleDefaultValueChange={handleDefaultValueChange}
            handleDefaultValueChangeWithAutoSave={handleDefaultValueChangeWithAutoSave}
            handleMapValues={handleMapValues}
            handleSaveAll={handleSaveAll}
            // Filters
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            mappingFilter={mappingFilter}
            setMappingFilter={setMappingFilter}
            // Source Fields
            sourceFields={sourceFields}
            isLoadingProperties={isLoadingProperties}
            // Solutions
            solutions={solutions}
            // UI State
            isSaving={isSaving}
            isAutoSaving={isAutoSaving}
            isLoadingProfiles={isLoadingProfiles}
            isLoadingFieldMappings={isLoadingFieldMappings}
            isDuplicating={isDuplicating}
            isDeleting={isDeleting}
            isMakingDefault={isMakingDefault}
            isCreatingProfile={isCreatingProfile}
            isSavingProfileName={isSavingProfileName}
            isEmptyMapping={isEmptyMapping}
          />
        )}

        {activeTab === "Automation & Alerts" && (
          <AutomationTab
            // Referral Settings
            autoAcceptAWSReferrals={autoAcceptAWSReferrals}
            setAutoAcceptAWSReferrals={setAutoAcceptAWSReferrals}
            // Automation Settings
            autoSyncOpportunity={autoSyncOpportunity}
            setAutoSyncOpportunity={setAutoSyncOpportunity}
            automatedOpportunitySharing={automatedOpportunitySharing}
            setAutomatedOpportunitySharing={setAutomatedOpportunitySharing}
            // Triggers
            triggers={triggers}
            handleCreateTrigger={handleCreateTrigger}
            handleEditTrigger={handleEditTrigger}
            handleDeactivateTrigger={handleDeactivateTrigger}
            handleDeleteTrigger={handleDeleteTrigger}
          />
        )}
      </div>

      {/* Modals */}
      <DisconnectModal
        visible={showDisconnectModal}
        onClose={() => setShowDisconnectModal(false)}
        onConfirm={handleDisconnect}
      />

      <DeleteMappingModal
        visible={showDeleteMappingModal}
        onClose={() => {
          setShowDeleteMappingModal(false);
          setDeleteTargetProfile(null);
          setDeleteTargetProfileId(null);
        }}
        onConfirm={confirmDeleteMapping}
        profileName={deleteTargetProfile?.name || ""}
      />

      <DuplicateMappingModal
        visible={showDuplicateMappingModal}
        onClose={() => setShowDuplicateMappingModal(false)}
        onConfirm={handleConfirmDuplicate}
        existingNames={Object.values(mappingNames)}
        originalName={mappingNames[activeProfile] || ""}
      />

      <AddMappingModal
        visible={showAddMappingModal}
        onClose={() => setShowAddMappingModal(false)}
        onConfirm={handleConfirmAddNew}
        existingNames={Object.values(mappingNames)}
      />

      <CreateTriggerModal
        visible={showCreateTriggerModal}
        onClose={() => {
          setShowCreateTriggerModal(false);
          setEditingTriggerId(null);
          setTriggerModalData(null);
        }}
        onSave={handleSaveTriggerWrapper}
        editingTriggerId={editingTriggerId}
        initialData={triggerModalData}
        showWarning={showWarning}
      />

      <PicklistMappingModal
        visible={showPicklistModal}
        onClose={() => {
          setShowPicklistModal(false);
          setPicklistModalData(null);
        }}
        onSave={handleSavePicklistMappings}
        targetFieldName={picklistModalData?.targetFieldName || ""}
        targetFieldLabel={picklistModalData?.targetFieldLabel || ""}
        sourceFieldApiName={picklistModalData?.sourceFieldApiName || ""}
        acePicklistValues={picklistModalData?.acePicklistValues || []}
        hubspotPicklistValues={picklistModalData?.hubspotPicklistValues || []}
        existingMappings={picklistModalData?.existingMappings || []}
        isDealStage={picklistModalData?.isDealStage || false}
        stagePipelines={picklistModalData?.stagePipelines || []}
        pipelinePicklistMappings={picklistModalData?.pipelinePicklistMappings || {}}
      />

      <ConnectedModal
        visible={showConnectedModal}
        onConfirm={handleConnectedModalConfirm}
      />

      <ConnectToAceModal
        visible={showConnectToAceModal}
        onClose={() => setShowConnectToAceModal(false)}
        onSave={handleSaveConnectToAce}
      />

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </PageBase>
  );
};

export default HubspotIntegration;
